import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { PatientForm } from "../components/PatientForm";
import { AttachmentsSection } from "../components/AttachmentsSection";
import { patientApi, attachmentsApi } from "../services/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ShieldCheck, CheckCircle2, Stethoscope, AlertTriangle } from "lucide-react";

// Generează un UUID robust (browserele moderne au crypto.randomUUID)
function makeSessionId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* fallback */ }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function PublicForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [sessionId, setSessionId] = useState(() => makeSessionId());
  const [attachments, setAttachments] = useState([]);

  // Reset sesiune + fișiere după fiecare submit reușit (pentru "Adaugă alt sportiv")
  const resetSession = () => {
    setSessionId(makeSessionId());
    setAttachments([]);
  };

  const handleSubmit = async (data) => {
    setIsLoading(true);
    try {
      const patient = await patientApi.create({ ...data, attachments_session_id: sessionId });
      setSubmitted(patient);
      if (patient.is_returning) {
        toast.success("Bine ai revenit! O consultație nouă a fost înregistrată.");
      } else {
        toast.success("Formularul a fost trimis cu succes!");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Failed to submit form:", error);
      const detail = error?.response?.data?.detail;
      toast.error(detail || "Eroare la trimiterea formularului. Încearcă din nou.");
    } finally {
      setIsLoading(false);
    }
  };

  const attachmentsSlot = useMemo(() => (
    <AttachmentsSection
      title="Atașează fișiere sau poze"
      description="Poți încărca analize, scrisori medicale, poze cu leziuni etc. (JPG, PNG, HEIC, PDF — maxim 10 fișiere × 10 MB)."
      attachments={attachments}
      onUpload={(file, onProgress) => attachmentsApi.publicUpload(sessionId, file, onProgress)}
      onDelete={(fileId) => attachmentsApi.publicDelete(sessionId, fileId)}
      onAttachmentAdded={(att) => setAttachments((prev) => [...prev, att])}
      onAttachmentRemoved={(id) => setAttachments((prev) => prev.filter((a) => a.id !== id))}
      canUpload={true}
      canDelete={true}
      emptyText="Momentan nu ai atașat niciun fișier (opțional)."
      testIdPrefix="public-attachments"
    />
  ), [attachments, sessionId]);

  // La montare, dacă din vreun motiv (refresh cu session_id păstrat) există fișiere pending, le încărcăm
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await attachmentsApi.publicList(sessionId);
        if (mounted && Array.isArray(list) && list.length) {
          setAttachments(list);
        }
      } catch (_) { /* nimic pentru sesiune nouă */ }
    })();
    return () => { mounted = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen" data-testid="public-form-page">
      {/* Header public — doar logo + buton ADMIN */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h2 className="text-base font-semibold leading-none">MedSport</h2>
              <p className="text-[10px] text-muted-foreground">Cabinet medicină sportivă</p>
            </div>
          </div>
          <Link to="/admin/login">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 border-primary/40 text-primary hover:bg-primary/10"
              data-testid="admin-btn"
            >
              <ShieldCheck className="w-4 h-4" />
              ADMIN
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {submitted ? (
          <div className="bg-white rounded-2xl border-2 border-emerald-200 shadow-lg p-8 sm:p-12 text-center max-w-2xl mx-auto" data-testid="submit-success">
            <div className="w-20 h-20 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-12 h-12 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-semibold tracking-tight mb-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
              {submitted.is_returning ? "Bine ai revenit!" : "Formular trimis cu succes!"}
            </h1>
            <p className="text-muted-foreground mb-4">
              {submitted.is_returning
                ? "Te-am identificat în baza de date. O nouă consultație a fost adăugată la istoricul tău."
                : "Înregistrarea ta a fost salvată în baza de date. Vei fi contactat pentru programare."}
            </p>
            <div className="bg-slate-50 rounded-lg p-4 inline-block mb-6">
              <p className="text-sm text-slate-600">
                {submitted.is_returning ? "Numărul tău existent:" : "Număr de înregistrare:"}
              </p>
              <p className="text-3xl font-bold text-primary">#{submitted.numar_ordine}</p>
              {submitted.is_returning && submitted.reviews && submitted.reviews.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  Consultații înregistrate: <strong>{submitted.reviews.length}</strong>
                </p>
              )}
              {submitted.attachments && submitted.attachments.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  Fișiere atașate: <strong>{submitted.attachments.length}</strong>
                </p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                onClick={() => { setSubmitted(null); resetSession(); window.scrollTo(0, 0); }}
                variant="outline"
                data-testid="new-submission"
              >
                Adaugă alt sportiv
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-6 sm:mb-8">
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                Înscriere Sportiv
              </h1>
              <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                Completează formularul medico-sportiv pentru evaluare clinică
              </p>
              <div
                className="mt-4 rounded-lg border-2 border-red-400 bg-red-50 p-3 sm:p-4 text-sm sm:text-base text-red-900 flex gap-3 shadow-sm"
                data-testid="atentie-notice"
                role="alert"
              >
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 mt-0.5 flex-shrink-0 text-red-600" />
                <p className="leading-snug">
                  <strong className="uppercase tracking-wide">Atenție!</strong> Vă rugăm completați{" "}
                  <strong>CORECT</strong> și <strong>COMPLET</strong> (toate câmpurile) datele cerute în formular,
                  care aparțin sportivului. În caz contrar, viza va fi eliberată în mod automat pe datele
                  eronate furnizate de dvs.
                </p>
              </div>
              <div
                className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs sm:text-sm text-amber-900 flex gap-2"
                data-testid="gdpr-notice"
              >
                <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Confidențialitate (GDPR):</strong> datele introduse aici (nume, prenume, CNP, telefon) sunt
                  transmise direct cabinetului medical și NU sunt memorate de browser. Formularul folosește mecanisme
                  anti-autofill pentru a împiedica afișarea datelor altor pacienți. Te rugăm să nu folosești salvarea
                  automată a parolelor pentru acest formular.
                </span>
              </div>
            </div>

            <PatientForm
              onSubmit={handleSubmit}
              isLoading={isLoading}
              submitLabel="Trimite formularul"
              publicMode={true}
              attachmentsSlot={attachmentsSlot}
            />
          </>
        )}
      </main>
    </div>
  );
}
